import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, TextField, Button, Typography, Stepper, Step, StepLabel,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, Checkbox,
  InputAdornment, IconButton, CircularProgress, Alert, Divider,
  Chip, Paper, Accordion, AccordionSummary, AccordionDetails, Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { signUpUser, getAvailableRoles, getAllModules, createModule, updateModule, deleteModule } from '../services/adminUserService';
import { lookupCompanyByVAT } from '../../customers/services/customerService';
import PermissionsMatrix from './PermissionsMatrix';
import ModuleManagementDialog from '../../access-management/components/ModuleManagementDialog';

// ─── Step definitions ────────────────────────────────────────────────────────
const STEPS = [
  { label: 'Account Info',       icon: PersonIcon },
  { label: 'Company Info',       icon: BusinessIcon },
  { label: 'Store Info',         icon: StoreIcon },
  { label: 'Role & Permissions', icon: ShieldIcon },
  { label: 'Review',             icon: CheckIcon },
];

const TEXT = (name, label, required = false, grid = 6, extra = {}) =>
  ({ name, label, required, grid, ...extra });

const ACCOUNT_FIELDS = [
  TEXT('Name',            'Full Name',         true),
  TEXT('Email',           'Email',             true,  6, { type: 'email' }),
  TEXT('Password',        'Password',          true,  6, { type: 'password' }),
  TEXT('ConfirmPassword', 'Confirm Password',  true,  6, { type: 'password' }),
  TEXT('PhoneNumber',     'Phone Number',      false, 6),
];

const COMPANY_FIELDS = [
  TEXT('CompanyName',          'Company Name',         false, 6),
  TEXT('BTWNumber',            'BTW / VAT Number',     false, 6),
  TEXT('IBANumber',            'IBAN Number',          false, 6),
  TEXT('BICNumber',            'BIC Number',           false, 6),
  TEXT('RPRNumber',            'RPR Number',           false, 6),
  TEXT('CompanyPeppolId',      'Company Peppol ID',    false, 6),
  TEXT('PeppolUserId',         'Peppol User ID',       false, 6),
  TEXT('PeppolUserMotdePasse', 'Peppol Password',      false, 6, { type: 'password' }),
  TEXT('Website',              'Website',              false, 6),
  TEXT('SendInvoiceLimit',     'Send Invoice Limit',   false, 6, { type: 'number' }),
  TEXT('SubscriptionFee',      'Subscription Fee',     false, 6, { type: 'number' }),
  TEXT('Street',               'Street',               false, 6),
  TEXT('StreetNumber',         'Street No.',           false, 3),
  TEXT('PostCode',             'Post Code',            false, 3),
  TEXT('City',                 'City',                 false, 6),
  TEXT('Country',              'Country',              false, 6),
  TEXT('Notes',                'Notes / Remarks',      false, 12, { multiline: true, rows: 3 }),
];

const STORE_FIELDS = [
  TEXT('storeName',    'Store Name',    true,  6),
  TEXT('storeCode',    'Store Code',    false, 6),
  TEXT('email',        'Store Email',   false, 6, { type: 'email' }),
  TEXT('phoneNumber',  'Phone Number',  false, 6),
  TEXT('street',       'Street',        false, 6),
  TEXT('streetNumber', 'Street No.',    false, 3),
  TEXT('postCode',     'Post Code',     false, 3),
  TEXT('city',         'City',          false, 6),
  TEXT('country',      'Country',       false, 6),
  TEXT('description',  'Description',   false, 12, { multiline: true, rows: 2 }),
];

const EMPTY_STORE = {
  storeName: '', storeCode: '', description: '',
  street: '', streetNumber: '', postCode: '', city: '', country: '',
  email: '', phoneNumber: '', ibaNumber: '', bicNumber: '', isActive: true,
};

const TOGGLES = [
  { name: 'IsSuperAdmin',             label: 'Super Admin',              color: '#f44336' },
  { name: 'IsCompanyAdmin',           label: 'Company Admin',            color: '#ff9800' },
  { name: 'SendWelcomeEmail',         label: 'Send Welcome Email',       color: '#4caf50' },
  { name: 'RequireEmailConfirmation', label: 'Require Email Confirmation', color: '#2196f3' },
];

const EMPTY_FORM = {
  Name: '', Email: '', Password: '', ConfirmPassword: '', CompanyName: '',
  BTWNumber: '', IBANumber: '', BICNumber: '', CompanyPeppolId: '',
  PeppolUserId: '', PeppolUserMotdePasse: '', PhoneNumber: '',
  Street: '', StreetNumber: '', PostCode: '', City: '', Country: '',
  Website: '', RoleName: '', CompanyId: '', Notes: '',
  IsSuperAdmin: false, IsCompanyAdmin: false,
  SendWelcomeEmail: true, RequireEmailConfirmation: false,
};

// ─── Main Component ──────────────────────────────────────────────────────────
const CreateUserForm = ({ onCreated, initialValues = {} }) => {
  const [activeStep, setActiveStep]     = useState(0);
  const [form, setForm]                 = useState(() => ({ ...EMPTY_FORM, ...initialValues }));
  const [storeForm, setStoreForm]       = useState(EMPTY_STORE);
  const [sameEmailAsAccount, setSameEmailAsAccount] = useState(false);
  const [samePhoneAsCompany, setSamePhoneAsCompany] = useState(false);
  const [showPw, setShowPw]             = useState({});
  const [roles, setRoles]               = useState([]);
  const [modules, setModules]           = useState([]);
  const [permissions, setPermissions]   = useState({});
  const [moduleLoading, setModuleLoading] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [vatSearching, setVatSearching] = useState(false);
  const [vatError, setVatError]         = useState('');
  const [error, setError]               = useState('');
  const [moduleDialog, setModuleDialog] = useState({ open: false, mode: 'create', module: null });

  // Sync initialValues reset
  useEffect(() => {
    setForm({ ...EMPTY_FORM, ...initialValues });
    setStoreForm(EMPTY_STORE);
    setSameEmailAsAccount(false);
    setSamePhoneAsCompany(false);
    setActiveStep(0);
    setError('');
  }, [JSON.stringify(initialValues)]); // eslint-disable-line

  // Load roles on mount
  useEffect(() => {
    getAvailableRoles()
      .then((res) => {
        // API returns { success, message, roles: [{name, displayName, isSystemRole}] }
        const list = res?.roles || res?.payload || res?.data || res || [];
        setRoles(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  // Reload modules (called on mount and after module CRUD)
  const reloadModules = useCallback(() => {
    setModuleLoading(true);
    getAllModules()
      .then((res) => {
        const list = res?.payload || res?.data || res || [];
        setModules(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setModuleLoading(false));
  }, []);

  useEffect(() => { reloadModules(); }, [reloadModules]);

  // Module management handlers
  const handleAddModule = useCallback(() => {
    setModuleDialog({ open: true, mode: 'create', module: null });
  }, []);

  const handleEditModule = useCallback((mod) => {
    setModuleDialog({ open: true, mode: 'edit', module: mod });
  }, []);

  const handleDeleteModule = useCallback(async (mod) => {
    const name = mod.ModuleName || mod.Name || 'this module';
    if (!window.confirm(`Delete module "${name}"? This cannot be undone.`)) return;
    try {
      await deleteModule(mod.Id || mod.id);
      toast.success(`Module "${name}" deleted`);
      reloadModules();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete module');
    }
  }, [reloadModules]);

  const handleSaveModule = useCallback(async (data) => {
    try {
      if (moduleDialog.mode === 'create') {
        await createModule(data);
        toast.success('Module created');
      } else {
        const id = moduleDialog.module?.Id || moduleDialog.module?.id;
        await updateModule(id, data);
        toast.success('Module updated');
      }
      setModuleDialog((d) => ({ ...d, open: false }));
      reloadModules();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save module');
      throw e;
    }
  }, [moduleDialog, reloadModules]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'BTWNumber') setVatError('');
  }, []);

  const handleVATLookup = async () => {
    const vatNumber = form.BTWNumber?.trim() || '';
    if (!vatNumber) {
      setVatError('Please enter a VAT number.');
      return;
    }

    const cleanVAT = vatNumber.replace(/^BE/i, '').trim();
    if (!/^\d+$/.test(cleanVAT)) {
      setVatError('Invalid VAT format. Only numbers are allowed.');
      return;
    }

    setVatSearching(true);
    setVatError('');
    try {
      const response = await lookupCompanyByVAT(cleanVAT);
      const data = response?.payload || response?.data?.payload || response?.data || response;

      if (!data?.Found) {
        setVatError('No company found with this VAT number.');
        return;
      }

      setForm((prev) => ({
        ...prev,
        BTWNumber: data.EnterpriseNumber || prev.BTWNumber,
        CompanyName: data.CompanyName || prev.CompanyName,
        Street: data.StreetAddress || prev.Street,
        StreetNumber: data.StreetNumber || prev.StreetNumber,
        PostCode: data.PostCode || prev.PostCode,
        City: data.City || prev.City,
        Country: data.FullAddress?.includes('BE') ? 'Belgium' : (data.Country || prev.Country),
      }));
      toast.success('Company data loaded successfully.');
    } catch (e) {
      setVatError(e?.response?.data?.message || 'Failed to validate VAT number. Please try again.');
    } finally {
      setVatSearching(false);
    }
  };

  // When "use account email" is ticked keep store email in sync with account email
  useEffect(() => {
    if (sameEmailAsAccount) {
      setStoreForm(prev => ({ ...prev, email: form.Email || '' }));
    }
  }, [sameEmailAsAccount, form.Email]);

  // When "use company phone" is ticked keep store phone in sync with company phone
  useEffect(() => {
    if (samePhoneAsCompany) {
      setStoreForm(prev => ({ ...prev, phoneNumber: form.PhoneNumber || '' }));
    }
  }, [samePhoneAsCompany, form.PhoneNumber]);

  const handleStoreChange = useCallback((e) => {
    const { name, value } = e.target;
    setStoreForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePermChange = useCallback((modName, permKey, value) => {
    setPermissions((prev) => ({
      ...prev,
      [modName]: { ...(prev[modName] || {}), [permKey]: value },
    }));
  }, []);

  // ── Validation per step ────────────────────────────────────────────────
  const validateStep = (step) => {
    if (step === 0) {
      if (!form.Name.trim())  return 'Full Name is required.';
      if (!form.Email.trim()) return 'Email is required.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Email)) return 'Invalid email address.';
      if (!form.Password)     return 'Password is required.';
      if (form.Password.length < 8) return 'Password must be at least 8 characters.';
      if (form.Password !== form.ConfirmPassword) return 'Passwords do not match.';
    }
    if (step === 1) {
      // Validate website URL if provided
      if (form.Website && form.Website.trim()) {
        const urlPattern = /^(https?:\/\/|ftp:\/\/).+/i;
        if (!urlPattern.test(form.Website.trim())) {
          return 'The Website field is not a valid fully-qualified http, https, or ftp URL.';
        }
      }
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep(activeStep);
    if (err) { setError(err); return; }
    setError('');
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => { setError(''); setActiveStep((s) => s - 1); };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      // ── Build ModulePermissions (PermissionLevel: 0=None,1=ReadOnly,2=ReadWrite,3=FullAccess)
      const modulePermissions = Object.entries(permissions)
        .map(([modName, perms]) => {
          const mod = modules.find(m => (m.ModuleName || m.Name) === modName);
          let level = 0;
          if (perms.CanDelete)                          level = 3; // FullAccess
          else if (perms.CanEdit || perms.CanCreate)   level = 2; // ReadWrite
          else if (perms.CanView)                      level = 1; // ReadOnly
          return {
            ModuleId:        mod?.Id || mod?.id || 0,
            ModuleName:      modName,
            PermissionLevel: level,
          };
        })
        .filter(p => p.PermissionLevel > 0);

      // ── Map storeForm camelCase → API Store* PascalCase prefix
      const storePayload = storeForm.storeName?.trim() ? {
        StoreName:        storeForm.storeName,
        StoreCode:        storeForm.storeCode        || undefined,
        StoreDescription: storeForm.description      || undefined,
        StoreStreet:      storeForm.street           || undefined,
        StoreStreetNumber:storeForm.streetNumber     || undefined,
        StorePostCode:    storeForm.postCode         || undefined,
        StoreCity:        storeForm.city             || undefined,
        StoreCountry:     storeForm.country          || undefined,
        StoreEmail:       storeForm.email            || undefined,
        StorePhoneNumber: storeForm.phoneNumber      || undefined,
        StoreIBANumber:   storeForm.ibaNumber        || undefined,
        StoreBICNumber:   storeForm.bicNumber        || undefined,
      } : {};

      const payload = {
        ...form,
        ConfirmPassword:          form.Password, // explicitly mirror to satisfy backend [Compare] validation
        CompanyId:                form.CompanyId ? Number(form.CompanyId) : undefined,
        ModulePermissions:        modulePermissions.length > 0 ? modulePermissions : undefined,
        UseRoleDefaultPermissions:modulePermissions.length === 0,
        ...storePayload,
      };

      // Sanity-check: ConfirmPassword must always be present and match Password
      if (!payload.ConfirmPassword) payload.ConfirmPassword = payload.Password;

      await signUpUser(payload);

      toast.success(`User "${form.Name}" created successfully!`);
      setForm({ ...EMPTY_FORM, ...initialValues });
      setStoreForm(EMPTY_STORE);
      setSameEmailAsAccount(false);
      setSamePhoneAsCompany(false);
      setPermissions({});
      setActiveStep(0);
      onCreated?.();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to create user';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const strengthScore = useMemo(() => {
    const p = form.Password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  }, [form.Password]);
  const strengthColor = ['', '#f44336', '#ff9800', '#ff9800', '#4caf50'][strengthScore];
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strengthScore];

  // ── Step renderers ─────────────────────────────────────────────────────
  const renderAccountInfo = () => (
    <Grid container spacing={2}>
      {ACCOUNT_FIELDS.map(({ name, label, required, grid, type }) => {
        const isPasswordField = type === 'password';
        const visible = showPw[name];
        return (
          <Grid item xs={12} sm={grid} key={name}>
            <TextField
              fullWidth size="small" label={label} name={name}
              required={required}
              type={isPasswordField ? (visible ? 'text' : 'password') : (type || 'text')}
              value={form[name]}
              onChange={handleChange}
              error={name === 'ConfirmPassword' && form.ConfirmPassword.length > 0 && form.Password !== form.ConfirmPassword}
              helperText={name === 'ConfirmPassword' && form.ConfirmPassword.length > 0 && form.Password !== form.ConfirmPassword ? 'Passwords do not match' : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              InputProps={isPasswordField ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPw((p) => ({ ...p, [name]: !p[name] }))}>
                      {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              } : undefined}
            />
            {/* Password strength indicator */}
            {name === 'Password' && form.Password.length > 0 && (
              <Box sx={{ mt: 0.75 }}>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
                  {[1,2,3,4].map((lvl) => (
                    <Box key={lvl} sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: strengthScore >= lvl ? strengthColor : '#e0e0e0', transition: 'all 0.2s' }} />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: strengthColor, fontWeight: 700 }}>{strengthLabel}</Typography>
              </Box>
            )}
          </Grid>
        );
      })}
    </Grid>
  );

  const renderCompanyInfo = () => (
    <Grid container spacing={2}>
      {COMPANY_FIELDS.map(({ name, label, required, grid, type, multiline, rows }) => (
        <Grid item xs={12} sm={grid} key={name}>
          {name === 'BTWNumber' ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                fullWidth size="small" label={label} name={name}
                required={required}
                value={form[name]}
                onChange={handleChange}
                error={Boolean(vatError)}
                helperText={vatError || 'Enter VAT with or without the BE prefix'}
                inputProps={{ maxLength: 20 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
              <Button
                variant="outlined"
                onClick={handleVATLookup}
                disabled={vatSearching || !form.BTWNumber?.trim()}
                startIcon={vatSearching ? <CircularProgress size={16} /> : null}
                sx={{ mt: 0.25, minWidth: 105, height: 40, textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
              >
                Validate
              </Button>
            </Box>
          ) : (
            <TextField
              fullWidth size="small" label={label} name={name}
              required={required}
              type={type || 'text'}
              multiline={!!multiline} rows={rows}
              value={form[name]}
              onChange={handleChange}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          )}
        </Grid>
      ))}
    </Grid>
  );

  const handleCopyCompanyAddress = useCallback((checked) => {
    if (!checked) return;
    setStoreForm((prev) => ({
      ...prev,
      street:       form.Street       || prev.street,
      streetNumber: form.StreetNumber || prev.streetNumber,
      postCode:     form.PostCode     || prev.postCode,
      city:         form.City         || prev.city,
      country:      form.Country      || prev.country,
    }));
  }, [form]);

  const renderStoreInfo = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <StoreIcon sx={{ color: '#667eea' }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Store Information</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
            A store will be created in the user's company and they will be automatically assigned to it. Store name is required; all other fields are optional.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Store Name + Code */}
        {STORE_FIELDS.filter(f => ['storeName','storeCode'].includes(f.name)).map(({ name, label, required, grid }) => (
          <Grid item xs={12} sm={grid} key={name}>
            <TextField
              fullWidth size="small" label={label} name={name}
              required={required}
              value={storeForm[name] ?? ''}
              onChange={handleStoreChange}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Grid>
        ))}

        {/* Store Email with "use account email" option */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small" label="Store Email" name="email" type="email"
            value={storeForm.email ?? ''}
            onChange={e => { setSameEmailAsAccount(false); handleStoreChange(e); }}
            disabled={sameEmailAsAccount}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          <FormControlLabel
            sx={{ mt: 0.5, ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={sameEmailAsAccount}
                onChange={e => setSameEmailAsAccount(e.target.checked)}
                sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' }, p: '4px' }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#667eea' }}>
                Same as account email{form.Email ? ` (${form.Email})` : ''}
              </Typography>
            }
          />
        </Grid>

        {/* Phone with "use company phone" option */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small" label="Phone Number" name="phoneNumber"
            value={storeForm.phoneNumber ?? ''}
            onChange={e => { setSamePhoneAsCompany(false); handleStoreChange(e); }}
            disabled={samePhoneAsCompany}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          <FormControlLabel
            sx={{ mt: 0.5, ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={samePhoneAsCompany}
                onChange={e => setSamePhoneAsCompany(e.target.checked)}
                sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' }, p: '4px' }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#667eea' }}>
                Same as company phone number{form.PhoneNumber ? ` (${form.PhoneNumber})` : ''}
              </Typography>
            }
          />
        </Grid>

        {/* Address section with copy toggle */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  onChange={(e) => handleCopyCompanyAddress(e.target.checked)}
                  sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                  Copy address from company info
                </Typography>
              }
            />
            {(form.Street || form.City) && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                ({[form.Street, form.StreetNumber, form.PostCode, form.City, form.Country].filter(Boolean).join(', ')})
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Address fields */}
        {STORE_FIELDS.filter(f => ['street','streetNumber','postCode','city','country','description'].includes(f.name)).map(({ name, label, required, grid, type, multiline, rows }) => (
          <Grid item xs={12} sm={grid} key={name}>
            <TextField
              fullWidth size="small" label={label} name={name}
              required={required} type={type || 'text'}
              multiline={!!multiline} rows={rows}
              value={storeForm[name] ?? ''}
              onChange={handleStoreChange}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderRolePermissions = () => (
    <Box>
      {/* Role + toggles */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', mb: 1.5, display: 'block' }}>
          Role Assignment
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                name="RoleName" value={form.RoleName} label="Role"
                onChange={handleChange}
                sx={{ borderRadius: 1.5 }}
              >
                <MenuItem value=""><em>— None —</em></MenuItem>
                {roles.map((r) => {
                  const val = r.name || r.Name || r;
                  const label = r.displayName || r.DisplayName || val;
                  return (
                    <MenuItem key={val} value={val}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          {TOGGLES.map(({ name, label, color }) => (
            <Grid item xs={12} sm={6} key={name}>
              <FormControlLabel
                control={
                  <Switch
                    size="small" name={name} checked={form[name]}
                    onChange={handleChange}
                    sx={{ '& .Mui-checked': { color } }}
                  />
                }
                label={<Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Permissions */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', display: 'block' }}>
              Module Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              Configure access per module. Leave unchecked to inherit from role.
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={handleAddModule}
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700, borderColor: '#667eea', color: '#667eea', '&:hover': { borderColor: '#764ba2', bgcolor: 'rgba(102,126,234,0.06)' } }}
          >
            Add Module
          </Button>
        </Box>
        <Box sx={{ maxHeight: 420, overflow: 'auto', borderRadius: 2 }}>
          <PermissionsMatrix
            modules={modules}
            permissions={permissions}
            onChange={handlePermChange}
            loading={moduleLoading}
            onEditModule={handleEditModule}
            onDeleteModule={handleDeleteModule}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderReview = () => {
    const activePerms = Object.entries(permissions).filter(([, p]) =>
      Object.values(p).some(Boolean)
    );
    
    // Determine permission level description
    const getPermissionSummary = () => {
      if (activePerms.length === 0) {
        return form.RoleName ? `Inherits default permissions from ${form.RoleName} role` : 'No specific permissions assigned';
      }
      const permsCount = activePerms.reduce((acc, [, p]) => {
        const count = Object.values(p).filter(Boolean).length;
        return acc + count;
      }, 0);
      return `${permsCount} custom permission${permsCount !== 1 ? 's' : ''} across ${activePerms.length} module${activePerms.length !== 1 ? 's' : ''}`;
    };

    return (
      <Box>
        {/* Summary Header */}
        <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Creation Summary</Typography>
          <Typography variant="body2" sx={{ opacity: 0.95 }}>
            The following will be created when you submit:
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {form.CompanyName && <Chip label="New Company" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} />}
            {storeForm.storeName && <Chip label="New Store" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} />}
            <Chip label="New User Account" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }} />
          </Box>
        </Paper>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 16, color: '#667eea' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>User Account</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {[['Name', form.Name], ['Email', form.Email], ['Phone', form.PhoneNumber]].map(([k, v]) => v ? (
                <Grid item xs={12} sm={6} key={k}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{v}</Typography>
                </Grid>
              ) : null)}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {form.CompanyName && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon sx={{ fontSize: 16, color: '#667eea' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Company (Will be Created)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                {[['Company Name', form.CompanyName], ['VAT/BTW', form.BTWNumber], ['IBAN', form.IBANumber], ['BIC', form.BICNumber], ['Website', form.Website], ['Street', form.Street], ['City', form.City], ['Country', form.Country]].map(([k, v]) => v ? (
                  <Grid item xs={12} sm={6} key={k}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{v}</Typography>
                  </Grid>
                ) : null)}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StoreIcon sx={{ fontSize: 16, color: '#667eea' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>
                Store Information
                {storeForm.storeName?.trim() && (
                  <Chip label="Will be created" size="small" sx={{ ml: 1, height: 16, fontSize: '0.62rem', bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                )}
                {!storeForm.storeName?.trim() && (
                  <Chip label="Skipped" size="small" sx={{ ml: 1, height: 16, fontSize: '0.62rem', bgcolor: '#fff3e0', color: '#e65100' }} />
                )}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {storeForm.storeName?.trim() ? (
              <Grid container spacing={1}>
                {[['Store Name', storeForm.storeName], ['Store Code', storeForm.storeCode], ['Email', storeForm.email], ['Phone', storeForm.phoneNumber], ['City', storeForm.city], ['Country', storeForm.country]].map(([k, v]) => v ? (
                  <Grid item xs={12} sm={6} key={k}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{v}</Typography>
                  </Grid>
                ) : null)}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                No store name provided — store creation will be skipped. You can assign the user to an existing store later.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon sx={{ fontSize: 16, color: '#667eea' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>User Permissions</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>ROLE & FLAGS</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {form.RoleName && <Chip label={form.RoleName} color="primary" size="small" />}
                {!form.RoleName && <Chip label="No Role" size="small" variant="outlined" />}
                {TOGGLES.filter(({ name }) => form[name]).map(({ name, label, color }) => (
                  <Chip key={name} label={label} size="small" sx={{ bgcolor: color, color: 'white', fontWeight: 600 }} />
                ))}
              </Box>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>PERMISSION SUMMARY</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: activePerms.length > 0 ? '#667eea' : 'text.secondary' }}>
                {getPermissionSummary()}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {activePerms.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#667eea' }}>
                Custom Module Permissions ({activePerms.length} module{activePerms.length !== 1 ? 's' : ''})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                {activePerms.map(([mod, perms]) => (
                  <Box key={mod} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#667eea', mb: 0.5 }}>{mod}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 1 }}>
                      {['CanView','CanEdit','CanCreate','CanDelete'].filter(k => perms[k]).map(k => (
                        <Chip 
                          key={k} 
                          label={k.replace('Can','')} 
                          size="small" 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem',
                            bgcolor: k === 'CanDelete' ? '#f44336' : k === 'CanCreate' ? '#4caf50' : k === 'CanEdit' ? '#ff9800' : '#2196f3',
                            color: 'white',
                            fontWeight: 600
                          }} 
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  };

  const stepContent = [renderAccountInfo, renderCompanyInfo, renderStoreInfo, renderRolePermissions, renderReview];

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map(({ label }, i) => (
          <Step key={label} completed={activeStep > i}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error banner */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Step content */}
      <Box sx={{ minHeight: 360 }}>
        {stepContent[activeStep]?.()}
      </Box>

      <Divider sx={{ my: 2.5 }} />

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || submitting}
          sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}
        >
          Back
        </Button>
        <Typography variant="caption" color="text.secondary">
          Step {activeStep + 1} of {STEPS.length}
        </Typography>
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained" onClick={handleNext}
            sx={{
              fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained" onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 3,
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)' },
              '&:disabled': { background: '#e0e0e0' },
            }}
          >
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        )}
      </Box>

      {/* Module Management Dialog */}
      <ModuleManagementDialog
        open={moduleDialog.open}
        mode={moduleDialog.mode}
        module={moduleDialog.module}
        onClose={() => setModuleDialog((d) => ({ ...d, open: false }))}
        onSave={handleSaveModule}
      />
    </Box>
  );
};

export default CreateUserForm;
